import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ShieldIcon, AlertCircleIcon, DollarSignIcon } from 'lucide-react';
interface SellWithUsProps {
  onStartSelling: () => void;
}
export const SellWithUs: React.FC<SellWithUsProps> = ({
  onStartSelling
}) => {
  return <div className="w-full bg-white dark:bg-[#0F1F1D]" data-id="element-2739">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden" data-id="element-2740">
        <div className="absolute inset-0 bg-gray-50 dark:bg-[#0A1C1A] z-0" data-id="element-2741"></div>
        {/* Background gradient */}
        <div className="absolute inset-0 overflow-hidden z-0" data-id="element-2742">
          <div className="absolute w-[800px] h-[800px] rounded-full bg-gradient-to-r from-[#0A3C52]/20 to-[#2DB489]/20 dark:from-[#267A67]/20 dark:to-[#4ED1A0]/20 blur-3xl opacity-60 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-2743"></div>
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-2744">
          <motion.div className="text-center max-w-4xl mx-auto" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-2745">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-transparent bg-clip-text" data-id="element-2746">
              Sell Your Digital Products on DGStack
            </h1>
            <p className="text-xl text-gray-600 dark:text-[#B0C1BD] mb-10" data-id="element-2747">
              Join thousands of creators and entrepreneurs selling premium
              digital products globally. Turn your expertise into a thriving
              business with our trusted marketplace.
            </p>
            <motion.button className="bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white px-8 py-4 rounded-full text-lg font-medium hover:shadow-xl transition-all" whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={onStartSelling} data-id="element-2748">
              Start Selling Now
            </motion.button>
          </motion.div>
        </div>
      </section>
      {/* Why Join Section */}
      <section className="py-20 bg-white dark:bg-[#162825]" data-id="element-2749">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2750">
          <motion.div className="text-center mb-16" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-2751">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-transparent bg-clip-text" data-id="element-2752">
              Why Join Our Marketplace
            </h2>
            <p className="text-xl text-gray-600 dark:text-[#B0C1BD] max-w-3xl mx-auto" data-id="element-2753">
              We've built the most creator-friendly platform for selling digital
              products
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-2754">
            {[{
            title: 'Global Reach',
            description: 'Reach thousands of creators, startups, and entrepreneurs looking for premium digital products',
            icon: <motion.div className="w-12 h-12 rounded-full bg-[#2DB489]/10 dark:bg-[#2DB489]/20 flex items-center justify-center mb-4" data-id="element-2755">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#2DB489] dark:text-[#4ED1A0]" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-2756">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-2757" />
                    </svg>
                  </motion.div>
          }, {
            title: 'Keep 80% Revenue',
            description: 'We take just 20% - one of the most competitive rates in the industry for the value we provide',
            icon: <motion.div className="w-12 h-12 rounded-full bg-[#2DB489]/10 dark:bg-[#2DB489]/20 flex items-center justify-center mb-4" data-id="element-2758">
                    <DollarSignIcon className="h-6 w-6 text-[#2DB489] dark:text-[#4ED1A0]" data-id="element-2759" />
                  </motion.div>
          }, {
            title: 'Built-in Delivery',
            description: 'We handle payments, delivery, and customer service so you can focus on creating great products',
            icon: <motion.div className="w-12 h-12 rounded-full bg-[#2DB489]/10 dark:bg-[#2DB489]/20 flex items-center justify-center mb-4" data-id="element-2760">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#2DB489] dark:text-[#4ED1A0]" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-2761">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" data-id="element-2762" />
                    </svg>
                  </motion.div>
          }, {
            title: 'Get Featured',
            description: 'Top products get featured in our homepage, bundles, and marketing campaigns',
            icon: <motion.div className="w-12 h-12 rounded-full bg-[#2DB489]/10 dark:bg-[#2DB489]/20 flex items-center justify-center mb-4" data-id="element-2763">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#2DB489] dark:text-[#4ED1A0]" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-2764">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" data-id="element-2765" />
                    </svg>
                  </motion.div>
          }, {
            title: 'Seller Dashboard',
            description: 'Track your sales, manage products, and get insights with our easy-to-use dashboard',
            icon: <motion.div className="w-12 h-12 rounded-full bg-[#2DB489]/10 dark:bg-[#2DB489]/20 flex items-center justify-center mb-4" data-id="element-2766">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#2DB489] dark:text-[#4ED1A0]" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-2767">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" data-id="element-2768" />
                    </svg>
                  </motion.div>
          }, {
            title: 'Community Support',
            description: 'Join our community of creators and get support, feedback, and collaboration opportunities',
            icon: <motion.div className="w-12 h-12 rounded-full bg-[#2DB489]/10 dark:bg-[#2DB489]/20 flex items-center justify-center mb-4" data-id="element-2769">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#2DB489] dark:text-[#4ED1A0]" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-2770">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" data-id="element-2771" />
                    </svg>
                  </motion.div>
          }].map((item, index) => <motion.div key={index} className="bg-white dark:bg-[#1A3330] p-8 rounded-2xl shadow-sm border border-gray-100 dark:border-[#267A67]/30 hover:shadow-lg transition-all" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} whileHover={{
            y: -5
          }} data-id="element-2772">
                {item.icon}
                <h3 className="text-xl font-semibold mb-3 text-gray-800 dark:text-[#F8FAF9]" data-id="element-2773">
                  {item.title}
                </h3>
                <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-2774">
                  {item.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>
      {/* Quality Rules Section */}
      <section className="py-20 bg-gray-50 dark:bg-[#0A1C1A]" data-id="element-2775">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2776">
          <motion.div className="text-center mb-16" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-2777">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-transparent bg-clip-text" data-id="element-2778">
              Quality Rules
            </h2>
            <p className="text-xl text-gray-600 dark:text-[#B0C1BD] max-w-3xl mx-auto" data-id="element-2779">
              We maintain high standards to ensure buyers get exceptional
              products
            </p>
          </motion.div>
          <div className="max-w-4xl mx-auto bg-white dark:bg-[#162825] rounded-2xl shadow-sm p-8 border border-gray-100 dark:border-[#267A67]/30" data-id="element-2780">
            <div className="space-y-6" data-id="element-2781">
              {[{
              title: 'Original & High-Quality',
              description: 'Products must be original, high-quality, and clearly documented',
              icon: <CheckIcon className="h-5 w-5 text-[#2DB489] dark:text-[#4ED1A0]" data-id="element-2782" />
            }, {
              title: 'No Stolen Content',
              description: 'No stolen or AI-repackaged content without significant original value',
              icon: <AlertCircleIcon className="h-5 w-5 text-[#2DB489] dark:text-[#4ED1A0]" data-id="element-2783" />
            }, {
              title: 'Safe & Functional',
              description: 'No malware, scams, or broken files - all products are tested',
              icon: <ShieldIcon className="h-5 w-5 text-[#2DB489] dark:text-[#4ED1A0]" data-id="element-2784" />
            }, {
              title: 'Documentation Required',
              description: 'All files must include a guide, video, or preview/demo for buyers',
              icon: <CheckIcon className="h-5 w-5 text-[#2DB489] dark:text-[#4ED1A0]" data-id="element-2785" />
            }].map((rule, index) => <motion.div key={index} className="flex items-start" initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} data-id="element-2786">
                  <div className="mt-1 bg-[#2DB489]/10 dark:bg-[#2DB489]/20 p-1 rounded-full mr-4" data-id="element-2787">
                    {rule.icon}
                  </div>
                  <div data-id="element-2788">
                    <h3 className="font-semibold text-lg text-gray-800 dark:text-[#F8FAF9]" data-id="element-2789">
                      {rule.title}
                    </h3>
                    <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-2790">
                      {rule.description}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
      {/* How It Works Section */}
      <section className="py-20 bg-white dark:bg-[#162825]" data-id="element-2791">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2792">
          <motion.div className="text-center mb-16" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-2793">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-transparent bg-clip-text" data-id="element-2794">
              How It Works
            </h2>
            <p className="text-xl text-gray-600 dark:text-[#B0C1BD] max-w-3xl mx-auto" data-id="element-2795">
              A simple process to start selling your digital products
            </p>
          </motion.div>
          <div className="max-w-5xl mx-auto" data-id="element-2796">
            <div className="relative" data-id="element-2797">
              {/* Vertical line */}
              <div className="absolute left-[28px] top-0 bottom-0 w-1 bg-gray-100 dark:bg-[#267A67]/30 hidden md:block" data-id="element-2798"></div>
              {[{
              title: 'Submit Your Product',
              description: 'Fill out the submission form with details about your product and upload your files',
              number: '1'
            }, {
              title: 'Review Process',
              description: 'Our team reviews your submission within 24–72 hours for quality and compliance',
              number: '2'
            }, {
              title: 'Product Goes Live',
              description: 'Once approved, your product is published and available for purchase on our marketplace',
              number: '3'
            }, {
              title: 'Get Paid',
              description: 'Payments go through LemonSqueezy or Stripe Connect with 80% commission to you',
              number: '4'
            }].map((step, index) => <motion.div key={index} className="flex items-start mb-12 relative" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.2
            }} data-id="element-2799">
                  <div className="flex-shrink-0 w-14 h-14 rounded-full bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] flex items-center justify-center text-white font-bold text-xl mr-6 z-10" data-id="element-2800">
                    {step.number}
                  </div>
                  <div className="pt-2" data-id="element-2801">
                    <h3 className="text-xl font-semibold mb-2 text-gray-800 dark:text-[#F8FAF9]" data-id="element-2802">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-2803">
                      {step.description}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white" data-id="element-2804">
        <div className="container mx-auto px-4 md:px-6 text-center" data-id="element-2805">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="max-w-3xl mx-auto" data-id="element-2806">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-2807">
              Ready to Start Selling?
            </h2>
            <p className="text-xl opacity-90 mb-8" data-id="element-2808">
              Join our community of creators and start earning from your digital
              products today.
            </p>
            <motion.button className="bg-white text-[#0A3C52] dark:text-[#2DB489] px-8 py-4 rounded-full text-lg font-medium hover:shadow-xl transition-all" whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={onStartSelling} data-id="element-2809">
              Submit Your Product
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>;
};